import React from 'react'
import { BottomNavigation, BottomNavigationAction, useTheme } from '@mui/material'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import { makeStyles } from 'tss-react/mui'
import { FeatureContentElementKey, NewDot, useHasNewFeature } from 'src/Libs/new-content'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

interface StyleProps {
  bottomBarZIndex: number
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  toolbar: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  bottomBar: {
    top: 'auto',
    bottom: 0,
    zIndex: props.bottomBarZIndex,
  },
  bottomNav: {
    flex: 1,
    backgroundColor: theme.palette.background.header,
    maxWidth: '100%',
  },
  label: {
    fontSize: '0.7rem',
    whiteSpace: 'nowrap',
    color: theme.palette.sidebar.inactive,
  },
  root: {
    color: theme.palette.sidebar.inactive,
  },
  disabled: {
    pointerEvents: 'none',
    color: theme.shape.bottomLabel.disabledColor,
    minWidth: 0,
    fontSize: 10,
  },
  selected: {
    fontWeight: 'bold',
    zIndex: 1,
    '&&': { color: theme.palette.sidebar.active },

    '& .MuiBottomNavigationAction-label': {
      fontSize: '0.7rem',
    },
    '& > svg': {
      color: theme.palette.sidebar.active,
    },
  },
  wrapper: {
    minWidth: 0,
    fontSize: 10,
    color: theme.palette.sidebar.inactive,
  },
}))

export interface Action {
  label: string
  icon: React.ReactNode
  onClick?: () => void
  disabled?: boolean
  pathname?: string
  hasNewContent?: boolean
  elementKeys?: FeatureContentElementKey[]
  id?: string
  dataQa?: string
}

export interface BottomButtonGroupProps {
  value: number
  onChange?: (value: number) => void
  actions: Action[]
  bottomNavClass?: string
  zIndex?: number
}

/**
 * BottomButtonGroup component renders a bottom navigation bar with buttons.
 */
const BottomButtonGroup: React.FC<BottomButtonGroupProps> = ({ value, onChange, actions, bottomNavClass, zIndex }) => {
  const { classes, cx } = useStyles({ bottomBarZIndex: zIndex ?? 9999 })
  const { handleUpdateContentVersion } = useHasNewFeature()
  const { t } = useCustomTranslation()
  const theme = useTheme()

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onNavigation = (v: any): void => {
    onChange?.(v)
    window.scrollTo(0, 0)
  }

  const handleOnClick = (action: Action): void => {
    action.onClick?.()
    const isMoreButton = action.label === t('framework.more')

    if (action.hasNewContent && action.elementKeys && !isMoreButton) {
      handleUpdateContentVersion(action.elementKeys)
    }
  }

  const renderIcon = (action: Action): React.ReactElement => {
    return (
      <>
        {action.icon}
        {action.hasNewContent && (
          <NewDot
            sx={{
              position: 'absolute',
              top: 8,
              right: 18,
              backgroundColor: theme.palette.sidebar.active,
            }}
          />
        )}
      </>
    )
  }

  return (
    <AppBar position="fixed" color="primary" className={classes.bottomBar}>
      <Toolbar className={classes.toolbar}>
        <BottomNavigation showLabels={false} value={value} className={cx(classes.bottomNav, bottomNavClass)} onChange={(e, v) => onNavigation(v)}>
          {actions.map((action, i) => (
            <BottomNavigationAction
              key={i}
              id={action.id}
              className={action.disabled ? classes.disabled : classes.wrapper}
              {...action}
              classes={{
                root: classes.root,
                selected: classes.selected,
                label: classes.label,
              }}
              onClick={() => handleOnClick(action)}
              icon={renderIcon(action)}
              showLabel
              data-qa={action.dataQa}
            />
          ))}
        </BottomNavigation>
      </Toolbar>
    </AppBar>
  )
}

export default BottomButtonGroup
